/**
 * Rendering the stored terms-acceptance record — #511.
 *
 * The dispute-evidence form asks a host to evidence that the buyer accepted
 * the Terms of Service; Stripe receives that answer as `customer_signature`.
 * Until #511 there was no record behind the question, so the host typed it
 * from memory. The API now attaches `buyer_terms_acceptance` to the dispute
 * detail and these helpers turn it into something a human can read and paste.
 *
 * Mirrors termsAcceptanceStatement() in
 * apps/api/src/legal/terms-acceptance.service.ts — the API applies the same
 * fallback when a host leaves the field blank.
 */

export type TermsAcceptanceEvidence = {
  accepted: boolean
  /** #512 — ACCOUNT holder, or the GUEST who actually bought this booking. */
  subject?: 'ACCOUNT' | 'GUEST' | null
  terms_version: string | null
  accepted_at: string | null
  source: string | null
  guest_email?: string | null
  ip_address: string | null
  user_agent: string | null
  is_placeholder_version: boolean
}

/** One-line answer to "did they accept?" for the evidence form. */
export function describeTermsAcceptance(
  evidence: TermsAcceptanceEvidence | null | undefined,
): string {
  if (!evidence) return 'No acceptance record could be loaded.'
  if (!evidence.accepted) {
    // Guest checkout has recorded consent since #512, so a blank now means the
    // booking predates these records — not that the buyer refused, and no
    // longer "guests never had one".
    return 'No acceptance on file. This booking predates acceptance records.'
  }
  const when = evidence.accepted_at
    ? new Date(evidence.accepted_at).toLocaleString('en-AU')
    : 'an unknown time'
  const from = evidence.ip_address ? ` from ${evidence.ip_address}` : ''
  // Naming the subject matters: "the cardholder accepted" reads very
  // differently if the acceptance belongs to an account that was created
  // months later by someone who might not be the cardholder at all.
  const who =
    evidence.subject === 'GUEST'
      ? `Guest buyer${evidence.guest_email ? ` (${evidence.guest_email})` : ''} accepted`
      : 'Account holder accepted'
  return `${who} version ${evidence.terms_version} on ${when}${from}.`
}

/**
 * Which auth route is a SIGN-UP — #512.
 *
 * /login and /signup render the same component (apps/web/src/app/login/
 * login-form.tsx; /signup/page.tsx imports it), so the route is the only thing
 * that distinguishes intent. The explicit-consent checkbox goes on the sign-up
 * routes ONLY: a returning user signing in at /login already accepted, and
 * re-gating them behind a tick every single login would be friction that buys
 * no additional consent.
 *
 * Covers /signup and its nested /signup/hostsignup.
 */
export function isSignupRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  return pathname === '/signup' || pathname.startsWith('/signup/')
}

/**
 * Whether the auth form's actions should be enabled — #512.
 *
 * On a sign-up route the box must be ticked first. Everywhere else the tick is
 * irrelevant, so this collapses to "not busy" and existing users are untouched.
 */
export function authActionsEnabled(input: {
  pathname: string | null | undefined
  termsTicked: boolean
  loading: boolean
}): boolean {
  if (input.loading) return false
  if (!isSignupRoute(input.pathname)) return true
  return input.termsTicked
}

/**
 * Supabase user_metadata the sign-up routes stamp when the box is ticked, read
 * back by the API's signupConsentSource() to record SIGNUP_CHECKBOX rather
 * than SIGNUP_CONSENT_GATE.
 *
 * Carried on the auth object rather than in browser storage on purpose: it has
 * to survive a magic-link round trip through an email client and an OAuth
 * redirect through a third party, and sessionStorage survives neither.
 */
export function termsConsentMetadata(now: Date = new Date()): {
  terms_accepted_at: string
} {
  return { terms_accepted_at: now.toISOString() }
}

/** The metadata key, so the callback exchanger can check before it writes. */
export const TERMS_ACCEPTED_AT_KEY = 'terms_accepted_at'

/**
 * The caveat that has to travel with a placeholder version. Until #505
 * publishes the documents, the record proves the buyer passed the consent gate
 * — not that they agreed to a specific published text. Worded without naming
 * signup since #512, because a guest record comes from checkout instead.
 */
export function termsAcceptanceCaveat(
  evidence: TermsAcceptanceEvidence | null | undefined,
): string | null {
  if (!evidence?.accepted || !evidence.is_placeholder_version) return null
  return 'The Terms of Service document is not published yet, so this records that the buyer passed the consent gate rather than agreement to a specific published version.'
}

/**
 * Text an admin can send to Stripe verbatim. Null when nothing is on file.
 * Names where the consent was given — "at account creation" would be a lie
 * about a guest who never made an account (#512).
 */
export function termsAcceptanceStatement(
  evidence: TermsAcceptanceEvidence | null | undefined,
): string | null {
  if (!evidence?.accepted || !evidence.accepted_at) return null
  const from = evidence.ip_address ? ` from IP ${evidence.ip_address}` : ''
  const where =
    evidence.subject === 'GUEST'
      ? `at guest checkout${evidence.guest_email ? ` as ${evidence.guest_email}` : ''}`
      : 'at account creation'
  return `Cardholder accepted the Path Collective Terms of Service and Privacy Policy (version ${evidence.terms_version}) ${where} on ${new Date(
    evidence.accepted_at,
  ).toISOString()}${from}.`
}
